import { useState, useRef, useEffect, useCallback } from "react";
import { Card, Input, List, Typography, Space } from "antd";
import dayjs from "dayjs";
import { useBrand } from "context/BrandContext";
import { useGetInputSuggestionsQuery } from "graphql/queries/get-input-suggestions.generated";
import { InputSuggestionType } from "constants/field-values";

const { TextArea } = Input;
const { Text } = Typography;

export interface Comment {
  id: string;
  step_number: number;
  comment: string;
  created_at: string | Date;
  user: {
    first_name: string;
    last_name: string;
    profile_photo?: string | null;
  };
}

interface CommentPopupProps {
  comments: Comment[];
  onAddComment: (text: string) => void;
  onClose: () => void;
  anchorElement?: HTMLElement | null;
  title?: string;
  loading?: boolean;
}

const CommentPopup = ({
  comments,
  onAddComment,
  onClose,
  anchorElement,
  title = 'Tell us what you think about this step.',
  loading = false,
}: CommentPopupProps) => {
  const { theme } = useBrand();
  const [commentText, setCommentText] = useState("");
  const popupRef = useRef<HTMLDivElement>(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        anchorElement &&
        !anchorElement.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, anchorElement]);

  // Position popup below anchor element with right edges aligned
  useEffect(() => {
    if (popupRef.current && anchorElement) {
      const anchorRect = anchorElement.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();

      // Align right edge of popup with right edge of anchor
      const left = anchorRect.right - popupRect.width;

      // Ensure popup doesn't go off the left edge of the screen
      const finalLeft = Math.max(16, left);
      popupRef.current.style.left = `${finalLeft}px`;

      // Position popup 30px below anchor
      const top = anchorRect.top + 30;

      // Ensure popup doesn't go off the bottom of the screen
      const maxTop = window.innerHeight - popupRect.height - 16;
      const finalTop = Math.min(top, maxTop);
      popupRef.current.style.top = `${finalTop}px`;
    }
  }, [anchorElement]);

  const handleOnErrorInputSuggestions = useCallback(
      (error) => {
        console.log("GetAiSuggestedQueries Error: ", error);
      },
      []
    );

  const { data: inputSuggestions, loading: qLoading } =
    useGetInputSuggestionsQuery({
      variables: {
        type: InputSuggestionType.GUIDE_COMMENT
      },
      errorPolicy: "none",
      fetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: true,
      onError: handleOnErrorInputSuggestions,
    });


  const handleSubmit = () => {
    if (commentText.trim()) {
      onAddComment(commentText.trim());
      setCommentText("");
    }
  };

  const handleSuggestionClick = useCallback((suggestion: string) => {
    setCommentText(suggestion.replace(/(\.\.\.)/, " ").replace(/(\!)/, "$1"));
  }, [setCommentText]);

  return (
    <div ref={popupRef} className="comment-popup">
      <Card
        className="comment-popup-card"
        title={
          <div className="comment-popup-card-header">
            {title}
          </div>
        }
      >
        <div className="comment-popup-comments-list">
          {comments.length === 0 ? (
            <div className={inputSuggestions?.input_suggestions.length ? "comment-popup-suggestions-container" : ''}>
              {(inputSuggestions?.input_suggestions || []).map(({ id, text }) => (
                <button
                  key={id}
                  className="comment-popup-suggestion-button"
                  onClick={() => handleSuggestionClick(text)}
                  disabled={loading}
                >
                  {text}
                </button>
              ))}
            </div>
          ) : (
            <List
              dataSource={comments}
              renderItem={(comment) => (
                <List.Item
                  key={comment.id}
                  style={{ padding: '12px 16px', border: 'none' }}
                >
                  <List.Item.Meta
                    avatar={
                      <img
                        src={comment.user?.profile_photo || `${process.env.PUBLIC_URL}${theme.noProfilePhotoIcon}`}
                        className="comment-popup-user-photo"
                      />
                    }
                    title={
                      <Space>
                        <div style={{ fontSize: 14, color: '#67748E', fontFamily: 'Steradian-Medium', fontWeight: 500 }}>
                          {comment.user?.first_name} {comment.user?.last_name}
                        </div>
                        <div style={{ fontSize: 12, color: '#67748E', fontFamily: 'Steradian-Light', fontWeight: 300 }}>
                          {dayjs(comment.created_at).format('MM/DD/YY')}
                        </div>
                      </Space>
                    }
                    description={
                      <Text style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                        {comment.comment}
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </div>

        <div className="comment-popup-form">
          <TextArea
            name="guide-comment-input"
            placeholder="Add comment"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            autoSize={{ maxRows: 1 }}
            disabled={loading}
          />
          <button onClick={handleSubmit} disabled={commentText.trim() === "" || loading}>
            <img src="/assets/images/icons/send-paper-airplane.svg" />
          </button>
        </div>
      </Card>
    </div>
  );
};

export default CommentPopup;
